import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t } from '../utils/i18n.js'
import styles from './login-page.css?inline'

const LOGIN_MUTATION = gql`
  mutation Login($username: String!, $password: String!, $totp: String) {
    login(username: $username, password: $password, totp: $totp) {
      token
    }
  }
`

const START_TOTP_SETUP_MUTATION = gql`
  mutation StartTotpSetup($username: String!, $password: String!) {
    startTotpSetup(username: $username, password: $password) {
      otpauthUrl
    }
  }
`

const CONFIRM_TOTP_SETUP_MUTATION = gql`
  mutation ConfirmTotpSetup($username: String!, $password: String!, $code: String!) {
    confirmTotpSetup(username: $username, password: $password, code: $code)
  }
`

@customElement('login-page')
export class LoginPage extends LitElement {
  @state() private username = ''
  @state() private password = ''
  @state() private totpCode = ''
  @state() private mfaRequired = false
  @state() private mfaSetupUrl = ''
  @state() private mfaQrDataUrl = ''
  @state() private error = ''
  @state() private loading = false

  connectedCallback() {
    super.connectedCallback()
    // Listen for i18n updates
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
    })
  }

  static styles = css`${unsafeCSS(styles)}`

  private async onSubmit(e: SubmitEvent) {
    e.preventDefault()
    this.error = ''
    this.loading = true
    try {
      const { data, error } = await apolloClient.mutate<{ login: { token: string } }>({
        mutation: LOGIN_MUTATION,
        variables: { username: this.username, password: this.password, totp: this.totpCode || null },
      })

      if (error) {
        const msg =
          (error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map(
            (e) => e.message
          ).join('. ') ?? error.message
        this.error = msg
        return
      }
      const token = data?.login?.token
      if (token) {
        localStorage.setItem('auth-token', token)
        // Dispatch custom event to notify header of login
        window.dispatchEvent(new CustomEvent('auth-changed', { detail: { loggedIn: true } }))
        window.history.pushState({}, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
        return
      }
      this.error = t('login.errorNoToken')
    } catch (err) {
      const message = err instanceof Error ? err.message : t('login.errorFailed')
      if (message.includes('mfa setup required')) {
        await this.startTotpSetup()
        return
      }
      if (message.includes('mfa required')) {
        this.mfaRequired = true
        this.error = '' // MFA prompt is not an error; .error would confuse E2E (race with totp input)
        return
      }
      if (message.includes('invalid mfa code')) {
        this.mfaRequired = true
        this.error = t('login.errorInvalidMfa')
        return
      }
      this.error = message
    } finally {
      this.loading = false
    }
  }

  private async startTotpSetup() {
    try {
      const { data } = await apolloClient.mutate<{ startTotpSetup: { otpauthUrl: string } }>({
        mutation: START_TOTP_SETUP_MUTATION,
        variables: { username: this.username, password: this.password },
      })
      const url = data?.startTotpSetup?.otpauthUrl ?? ''
      this.mfaSetupUrl = url
      this.mfaQrDataUrl = ''
      this.mfaRequired = true
      this.error = url ? '' : t('login.errorMfaSetup')
      if (url) {
        try {
          const qrcode = await import('qrcode')
          const toDataURL = (qrcode as { toDataURL?: (text: string, opts?: { width?: number; margin?: number }) => Promise<string> }).toDataURL
          if (toDataURL) {
            this.mfaQrDataUrl = await toDataURL(url, { width: 200, margin: 2 })
          }
        } catch {
          // QR module unavailable; user can still use manual entry
        }
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('login.errorMfaSetup')
    }
  }

  /** Secret for manual entry (parsed from otpauth URL). */
  private get mfaManualSecret(): string {
    if (!this.mfaSetupUrl) return ''
    try {
      const u = new URL(this.mfaSetupUrl)
      return u.searchParams.get('secret') ?? ''
    } catch {
      return ''
    }
  }

  private async confirmTotpSetup() {
    this.error = ''
    this.loading = true
    try {
      const { data, error } = await apolloClient.mutate<{ confirmTotpSetup: boolean }>({
        mutation: CONFIRM_TOTP_SETUP_MUTATION,
        variables: { username: this.username, password: this.password, code: this.totpCode },
      })
      if (error) {
        const msg =
          (error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map(
            (e) => e.message
          ).join('. ') ?? error.message
        this.error = msg
        return
      }
      if (!data?.confirmTotpSetup) {
        this.error = t('login.errorInvalidMfa')
        return
      }
      await this.onSubmit(new SubmitEvent('submit'))
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('login.errorMfaSetup')
    } finally {
      this.loading = false
    }
  }

  render() {
    return html`
      <div class="page">
        <h1>${t('login.title')}</h1>
        <form class="form" @submit=${this.onSubmit}>
          <div class="field">
            <label for="username">${t('login.username')}</label>
            <input
              id="username"
              type="text"
              data-testid="login-username"
              .value=${this.username}
              @input=${(e: Event) => {
                this.username = (e.target as HTMLInputElement).value
              }}
              placeholder=${t('login.usernamePlaceholder')}
              autocomplete="username"
              ?disabled=${this.loading}
            />
          </div>
          <div class="field">
            <label for="password">${t('login.password')}</label>
            <input
              id="password"
              type="password"
              data-testid="login-password"
              .value=${this.password}
              @input=${(e: Event) => {
                this.password = (e.target as HTMLInputElement).value
              }}
              placeholder=${t('login.passwordPlaceholder')}
              autocomplete="current-password"
              ?disabled=${this.loading}
            />
          </div>
          ${this.mfaRequired ? html`
            <div class="mfa-block">
              <h2>${t('login.mfaTitle')}</h2>
              ${this.mfaSetupUrl ? html`
                <p class="mfa-hint">${t('login.mfaScanQr')}</p>
                ${this.mfaQrDataUrl
                  ? html`<div class="mfa-qr-wrap"><img class="mfa-qr" src=${this.mfaQrDataUrl} alt=${t('login.mfaQrAlt')} /></div>`
                  : html`<div class="mfa-qr-placeholder">${t('login.mfaQrGenerating')}</div>`}
                ${this.mfaManualSecret ? html`
                  <details class="mfa-manual">
                    <summary>${t('login.mfaManualEntry')}</summary>
                    <p class="mfa-hint">${t('login.mfaManualHint')}</p>
                    <code class="mfa-secret">${this.mfaManualSecret}</code>
                  </details>
                ` : ''}
              ` : ''}
              <div class="field">
                <label for="totp">${t('login.mfaCode')}</label>
                <input
                  id="totp"
                  type="text"
                  inputmode="numeric"
                  data-testid="login-totp"
                  .value=${this.totpCode}
                  @input=${(e: Event) => {
                    this.totpCode = (e.target as HTMLInputElement).value
                  }}
                  placeholder=${t('login.mfaCodePlaceholder')}
                  autocomplete="one-time-code"
                  ?disabled=${this.loading}
                />
              </div>
              ${this.mfaSetupUrl ? html`
                <button type="button" class="submit" ?disabled=${this.loading || !this.totpCode.trim()}
                  @click=${this.confirmTotpSetup}>
                  ${t('login.mfaVerify')}
                </button>
              ` : ''}
            </div>
          ` : ''}
          <button type="submit" class="submit" data-testid="login-submit" ?disabled=${this.loading}>
            ${this.loading ? t('login.signingIn') : t('login.signIn')}
          </button>
          ${this.error ? html`<p class="error">${this.error}</p>` : ''}
        </form>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-page': LoginPage
  }
}
